import {
  useNavigate, useRouterState, useChildMatches,
} from '@tanstack/react-router';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { layoutRoute } from '@app/router/layout';
import {
  ProposalCreationRoute,
  ProposalCreationClientAndMortgageRoute,
  ProposalCreationBankProductsRoute,
  ProposalCreationConfiguratorRoute,
  ProposalsListRoute
} from '@modules/proposals/presentation/routes/index';
import { ProposalEntity } from '@modules/clients/entities/client/api/dto/clientProposal.dto';
import { toast } from '@huspy/forge/shared';
import { ProposalDomain } from '@modules/proposals/domain/proposal';
import useComputeCaseCreationState from './useComputeProposalCreationState';

export namespace ProposalCreationEngine {
  export type StateMachineRoutes = typeof ProposalCreationClientAndMortgageRoute |
  typeof ProposalCreationBankProductsRoute | typeof ProposalCreationConfiguratorRoute;
  export type StateMachine = Record<StateMachineRoutes['to'],
  {
    isAccessible: boolean;
    isComplete?: boolean;
    isAgregateRoute?: boolean;
    isSkipped?: boolean;
    stateMetadata?: {
      completedSteps: number;
      totalSteps: number;
    };
  }>;
}

const useProposalCreationStateMachineEngine = ({ isEnabled, proposal }: {
  proposal?: ProposalEntity,
  isEnabled?: boolean
// eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const route = ProposalCreationRoute;
  const { id } = route.useParams();
  const routerState = useRouterState();
  const navigate = useNavigate();
  const [latestStateIndex, setLatestStateIndex] = useState<number>(0);

  const isRootRoute = useChildMatches().length === 0;

  const { STATE_MACHINE: _STATE_MACHINE } = useComputeCaseCreationState({ proposal });

  const STATE_MACHINE_ROUTES = useMemo(() => Object.keys(_STATE_MACHINE).filter((val) =>
    val !== ProposalCreationRoute.to
  && (_STATE_MACHINE[val as keyof typeof _STATE_MACHINE].isSkipped === undefined
    || !_STATE_MACHINE[val as keyof typeof _STATE_MACHINE].isSkipped
  )), [_STATE_MACHINE]);

  if (!id) {
    navigate({ to: ProposalsListRoute.to });
  }

  const determineLatestState = useCallback(() => (STATE_MACHINE_ROUTES as unknown as (keyof typeof _STATE_MACHINE)[])
    .reduceRight((currentValue, nextValue) => {
      if (currentValue !== null) return currentValue;
      return (_STATE_MACHINE[nextValue].isAccessible ? nextValue : currentValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, null as unknown as keyof typeof _STATE_MACHINE), [_STATE_MACHINE]);

  // eslint-disable-next-line max-len, unicorn/prefer-at
  const currentStateIndex = STATE_MACHINE_ROUTES.indexOf(routerState.matches[routerState.matches.length - 1]?.routeId?.replace(layoutRoute.id, '')!);
  const upcomingStateIndex = currentStateIndex + 1;
  // eslint-disable-next-line max-len
  const isNextStateAccessible = _STATE_MACHINE[STATE_MACHINE_ROUTES[upcomingStateIndex] as (keyof typeof _STATE_MACHINE)]?.isAccessible;

  const unlockNextStateAndNavigate = () => {
    if (latestStateIndex === currentStateIndex) {
      setLatestStateIndex(upcomingStateIndex);
      setTimeout(() => { navigate({ to: STATE_MACHINE_ROUTES[upcomingStateIndex], params: { id } }); }, 500);
    } else {
      navigate({ to: STATE_MACHINE_ROUTES[upcomingStateIndex], params: { id } });
    }
  };

  const goToNextState = () => {
    if (currentStateIndex === STATE_MACHINE_ROUTES.length - 1
      || (upcomingStateIndex > latestStateIndex)) {
      return;
    }

    navigate({ to: STATE_MACHINE_ROUTES[upcomingStateIndex], params: { id } });
  };

  const goToPreviousState = () => {
    if (currentStateIndex === 0) {
      return;
    }

    navigate({ to: STATE_MACHINE_ROUTES[currentStateIndex - 1], params: { id } });
  };

  const goToState = (stateIdx: number) => {
    if (stateIdx > STATE_MACHINE_ROUTES.length - 1 || stateIdx > latestStateIndex) {
      return;
    }
    navigate({ to: STATE_MACHINE_ROUTES[stateIdx], params: { id } });
  };

  useEffect(() => {
    setLatestStateIndex(STATE_MACHINE_ROUTES.indexOf(determineLatestState()));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [STATE_MACHINE_ROUTES]);

  // This useEffect handles redirection in case if the state is not accessible
  useEffect(() => {
    if (!isEnabled || latestStateIndex === -1) {
      return;
    }

    if (!proposal) {
      navigate({ to: ProposalsListRoute.to });
      return;
    }

    if (!ProposalDomain.canEdit(proposal)) {
      toast('neutral', { message: 'Proposal locked' });
      navigate({ to: ProposalsListRoute.to });
      return;
    }

    // routerState matches have prepended /layout in the path, so it has to be stripped
    // eslint-disable-next-line max-len, unicorn/prefer-at
    const stateTryingToAccess = (routerState.matches[routerState.matches.length - 1]?.routeId)?.replace(layoutRoute.id, '') as unknown as keyof typeof _STATE_MACHINE;
    const stateTryingToAccessIndex = STATE_MACHINE_ROUTES.indexOf(stateTryingToAccess);

    // check if route can be accesed, if yes do a return
    if (!isRootRoute && stateTryingToAccess && (_STATE_MACHINE[stateTryingToAccess]?.isAccessible
      || stateTryingToAccessIndex <= latestStateIndex)) {
      return;
    }

    // eslint-disable-next-line unicorn/prefer-at
    if ((routerState.matches[routerState.matches.length - 1]?.routeId as string) !== determineLatestState()) {
      navigate({
        to: determineLatestState(),
        params: { id },
        replace: true,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, determineLatestState, proposal, routerState.matches, isRootRoute, latestStateIndex]);

  return {
    unlockNextStateAndNavigate,
    goToNextState,
    goToPreviousState,
    isNextStateAccessible,
    goToState,
    latestStateIndex,
    _STATE_MACHINE,
    currentStateIndex,
  };
};

export default useProposalCreationStateMachineEngine;
